import { validateApplication } from 'api/ats';
import type { ApiListingApplicationListingType } from 'api/types/ApiListingApplicationListingType';
import type { ApiRequestedAttachment } from 'api/types/ApiRequestedAttachment';
import type { ApiUser } from 'api/userEnvironment/types/ApiUserEnvironment';
import { Box } from 'components/Box/Box';
import { CheckboxField } from 'components/Form/CheckboxField';
import { FilePickerField } from 'components/Form/FileUploadField';
import { useFormContext } from 'components/Form/FormContext';
import { HiddenField } from 'components/Form/HiddenField';
import { InputField } from 'components/Form/InputField';
import { MultiInputField } from 'components/Form/MultiInputField';
import { LegacyTooltip } from 'components/LegacyTooltip/LegacyTooltip';
import { RequestedAttachmentUserField } from 'modules/listing/components/ApplicationFields/RequestedAttachmentUserField';
import { RequestedTextUserField } from 'modules/listing/components/ApplicationFields/RequestedTextUserField';
import { RequestedURLUserField } from 'modules/listing/components/ApplicationFields/RequestedURLUserField';

import { RequestedMultipleOptionField } from './RequestedMultipleOptionField';
import { RequestedSingleOptionField } from './RequestedSingleOptionField';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isNewResume = (user: ApiUser | null | undefined, getValue: any) =>
  user?.resumeId &&
  getValue('resumeS3FileId') &&
  getValue('resumeS3FileId') !== user.resumeId;

type Props = {
  user: ApiUser | null;
  listingType: ApiListingApplicationListingType;
  isPreview: boolean;
  allFieldsRequired: boolean;
  requestedAttachments: ApiRequestedAttachment[];
  orgAdminManuallyAddingApplicant?: boolean;
};

export function ListingApplicationFields({
  user,
  listingType,
  isPreview,
  allFieldsRequired,
  requestedAttachments,
  orgAdminManuallyAddingApplicant,
}: Props) {
  const { handleError, getValue, updateValues, clearErrors } = useFormContext();

  return (
    <>
      <HiddenField name="requestedAttachmentsHash" />

      <InputField
        disabled={isPreview}
        name="firstName"
        label={
          !allFieldsRequired
            ? `${getText('First Name')}*`
            : `${getText('First Name')}`
        }
        placeholder={getText('Casey')}
      />

      <InputField
        disabled={isPreview}
        name="lastName"
        label={
          !allFieldsRequired
            ? `${getText('Last Name')}*`
            : `${getText('Last Name')}`
        }
        placeholder={getText('Smith')}
      />

      <InputField
        disabled={isPreview}
        name="email"
        label={
          !allFieldsRequired ? `${getText('Email')}*` : `${getText('Email')}`
        }
        type="email"
        placeholder={getText('casey.smith@example.com')}
      />

      {/*
         NOTE: the requestedAttachments field is used four times here:
         once for type="TEXT", once for type="URL", once for type="FILE",
         once for type="OPTION_SINGLE" and once for type="OPTION_MANY". In each
         instance, the other types will be null.
        */}

      {requestedAttachments.length > 0 && (
        <MultiInputField
          hideControls
          component={RequestedTextUserField}
          maxInputs={requestedAttachments.length}
          name="requestedAttachments"
          extraData={requestedAttachments.map((ra) => ({
            ...ra,
            label:
              !allFieldsRequired && ra.isRequired ? `${ra.label}*` : ra.label,
            disabled: isPreview,
          }))}
        />
      )}

      {requestedAttachments.length > 0 && (
        <MultiInputField
          hideControls
          component={RequestedSingleOptionField}
          maxInputs={requestedAttachments.length}
          name="requestedAttachments"
          extraData={requestedAttachments.map((ra) => ({
            ...ra,
            label:
              !allFieldsRequired && ra.isRequired ? `${ra.label}*` : ra.label,
            disabled: isPreview,
          }))}
        />
      )}

      {requestedAttachments.length > 0 && (
        <MultiInputField
          hideControls
          component={RequestedMultipleOptionField}
          maxInputs={requestedAttachments.length}
          name="requestedAttachments"
          extraData={requestedAttachments.map((ra) => ({
            ...ra,
            label:
              !allFieldsRequired && ra.isRequired ? `${ra.label}*` : ra.label,
            disabled: isPreview,
          }))}
        />
      )}

      {requestedAttachments.length > 0 && (
        <MultiInputField
          hideControls
          component={RequestedURLUserField}
          maxInputs={requestedAttachments.length}
          name="requestedAttachments"
          extraData={requestedAttachments.map((ra) => ({
            ...ra,
            label:
              !allFieldsRequired && ra.isRequired ? `${ra.label}*` : ra.label,
            disabled: isPreview,
          }))}
          placeholder={getText('https://example.org')}
        />
      )}

      {listingType !== 'VOLOP' ? (
        <FilePickerField
          disabled={isPreview}
          label={
            !allFieldsRequired
              ? `${getText('Resume')}*`
              : `${getText('Resume')}`
          }
          description={getText('Resume must be uploaded in PDF format')}
          downloadAttachment={false}
          name="resumeS3FileId"
          fileType={
            orgAdminManuallyAddingApplicant
              ? 'LISTING_APPLICATION'
              : 'USER_RESUME'
          }
          accept=".pdf"
          showDate={!isNewResume(user, getValue)}
          onUpdateValue={async (fileId) => {
            try {
              if (!orgAdminManuallyAddingApplicant) {
                await validateApplication(
                  { resumeS3FileId: fileId },
                  { partial: true },
                );
              }
              updateValues({ resumeS3FileId: fileId });
              clearErrors();
            } catch (error) {
              handleError(error as Error, { resumeS3FileId: fileId });
            }
          }}
          extraAnalyticsData={{
            context: 'Listing Application Form',
            listing_type: listingType,
            file_label: 'Resume',
          }}
        />
      ) : null}

      {user && isNewResume(user, getValue) && (
        <Box mt={-26}>
          <CheckboxField
            disabled={isPreview}
            name="replaceCurrentResume"
            label={
              <Box display="flex" alignItems="center">
                {getText('Replace my current resume on file with this one')}
                <Box position="relative" zIndex={1} mt="1px" ml="6px">
                  <LegacyTooltip>
                    {getText(`You already have a resume saved with your account.
                              If you check this box, we'll save this new resume
                              to your account.`)}
                  </LegacyTooltip>
                </Box>
              </Box>
            }
          />
        </Box>
      )}

      {requestedAttachments.length > 0 && (
        <MultiInputField
          hideControls
          component={RequestedAttachmentUserField}
          maxInputs={requestedAttachments.length}
          name="requestedAttachments"
          extraData={requestedAttachments.map((ra) => ({
            ...ra,
            analyticsLabel: ra.label,
            label:
              !allFieldsRequired && ra.isRequired ? `${ra.label}*` : ra.label,
            downloadAttachment: false,
            fileType: 'LISTING_APPLICATION',
            disabled: isPreview,
            listingType,
          }))}
        />
      )}
    </>
  );
}
